import { CoreButton, CoreStatusView, MMButton } from '@components'
import CorePreviewLine from '@components/CorePreviewLine'
import CustomPreloader from '@components/CustomPreloader'
import { getAccountingCategoryLabel } from '@constants'
import AnnounceIcon from '@images/icons/announce-icon.svg?react'
import PayoutIcon from '@images/icons/arrow-up.svg?react'
import RefundIcon from '@images/icons/refund-icon.svg?react'
import BackArrowIcon from '@images/icons/transaction-details-back-arrow-left-icon.svg?react'
import TransactionsExportDocumentIcon from '@images/icons/transaction-export-icon.svg?react'
import { DocumentRequestUploadModal } from '@modules/transactions/components/DocumentRequestUploadModal'
import { cutCryptoAddress, formatCurrencyForDisplay, formatTransactionDescriptionForDisplay } from '@utils'
import cn from 'classnames'
import {
  PaymentFlows,
  PaymentMethods,
  TransactionDirections,
  TransactionStatuses,
  useCurrencies,
  useDownloadTransactionAttachment,
  useDownloadTransactionReceipt,
  useFeatureFlagState,
  useMerchantQry,
  usePayoutInitiateMutation,
  usePayoutPreviewMutation,
  useRefundInitiateMutation,
  useRefundPreviewMutation,
  useTransactionDetailsQry
} from 'mmfintech-backend-api'
import type { TransactionStatus } from 'mmfintech-backend-api/types'
import {
  cleanupUnderscores,
  formatDate,
  formatDateTime,
  formatFloat,
  GlobalContext,
  isValidArray,
  isValidString,
  tr
} from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useContext, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { HashLink } from './elements/HashLink'
import { PayoutModal } from './elements/PayoutModal'
import { RefundModal } from './elements/RefundModal'

import './styled/transactionsDetails.scss'

function TransactionDetails({ isModal = false, transactionId = null }) {
  const { modalShow } = useContext(GlobalContext)

  const [currentTransactionId, setCurrentTransactionId] = useState(transactionId)

  const history = useHistory()

  const { merchant } = useMerchantQry()
  const { getCurrencyPrecision } = useCurrencies()
  const { transaction, transactionError, transactionFetching } = useTransactionDetailsQry(currentTransactionId)

  const accountingCategoryFF = useFeatureFlagState('WITHDRAW_ACCOUNTING_CATEGORY')

  const { downloadAttachment } = useDownloadTransactionAttachment()
  const { downloadReceipt, downloadReceiptFetching } = useDownloadTransactionReceipt()

  const [_refundPreview, { reset: resetRefundPreview }] = useRefundPreviewMutation({
    fixedCacheKey: 'refund-preview'
  })
  const [_refundInit, { reset: resetRefundInitiation }] = useRefundInitiateMutation({
    fixedCacheKey: 'refund-init'
  })
  const [_payoutPreview, { reset: resetPayoutPreview }] = usePayoutPreviewMutation({
    fixedCacheKey: 'payout-preview'
  })
  const [_payoutInit, { reset: resetPayoutInitiation }] = usePayoutInitiateMutation({
    fixedCacheKey: 'payout-init'
  })

  const authorizationDate =
    transaction?.paymentFlow !== PaymentFlows.CARD_AUTHORIZATION
      ? (transaction?.cardPaymentDetails?.authorizationDetails?.authorizationDate ??
        transaction?.cardPaymentDetails?.authorizationDetails?.authorizationPosDate)
      : undefined

  const transactionType =
    typeof transaction?.trnTypeLocalizationKey === 'string'
      ? transaction.trnTypeLocalizationKey.split('.').pop()
      : undefined

  const showProcessingAmount: boolean =
    Boolean(transaction?.cardPaymentDetails?.transactionAmount) &&
    transaction?.cardPaymentDetails?.transactionCurrency !== transaction?.currency

  const showAdditionalActions =
    transaction?.status === TransactionStatuses.PROCESSED &&
    transaction?.direction === TransactionDirections.DEPOSIT &&
    ADDITIONAL_ACTION_FLOWS.includes(transaction?.paymentFlow) &&
    ADDITIONAL_ACTION_METHODS.includes(transaction?.paymentMethod ?? '')

  const showRefund = showAdditionalActions && merchant?.capabilities?.enableRefunds
  const showPayout = showAdditionalActions && merchant?.capabilities?.enablePayouts

  const handlePayout = () => {
    modalShow({
      header: tr('FRONTEND.TRANSACTIONS.PAYOUT_BUTTON', 'Payout'),
      content: <PayoutModal transaction={transaction} />,
      options: {
        onClose: () => {
          resetPayoutPreview()
          resetPayoutInitiation()
        }
      }
    })
  }

  const handleRefund = () => {
    modalShow({
      header: tr('FRONTEND.TRANSACTIONS.REFUND_BUTTON', 'Refund'),
      content: <RefundModal transaction={transaction} />,
      options: {
        onClose: () => {
          resetRefundPreview()
          resetRefundInitiation()
        }
      }
    })
  }

  return (
    <div className={cn('transaction-details-container', { 'is-modal': isModal })}>
      {!isModal && (
        <>
          <div className='transaction-details-header-wrapper'>
            <CoreButton
              text={tr('FRONTEND.BUTTONS.BACK', 'Back')}
              data-test='transaction-details-back-button'
              LeftIcon={<BackArrowIcon />}
              style={{ marginRight: '2rem' }}
              className='transaction-details-header-back-button'
              variation='secondary'
              onClick={() => history.goBack()}
            />
            {transaction?.paymentMethod === PaymentMethods.BANKWIRE && (
              <CoreButton
                data-test='transaction-details-PDF-download-button'
                text={tr('FRONTEND.TRANSACTIONS.DETAILS.BUTTON.DOWNLOAD', 'Download as PDF')}
                LeftIcon={<TransactionsExportDocumentIcon />}
                onClick={() => downloadReceipt(transaction.id)}
                variation='secondary'
                className='transaction-details-header-PDF-button'
              />
            )}
          </div>
          <h3 data-test='transaction-details-title'>
            {tr('FRONTEND.TRANSACTIONS.DETAILS.TITLE', 'Transaction details')}
          </h3>
        </>
      )}

      {transactionFetching && <CustomPreloader />}
      <ErrorDisplay error={transactionError} />

      <div className='transaction-details-wrapper'>
        {Boolean(transaction?.amount) && (
          <div className='transaction-details-content-header-wrapper'>
            <span data-test='transaction-details-amount-label' className='transaction-details-header'>
              {tr('FRONTEND.TRANSACTIONS.DETAILS.AMOUNT', 'Amount')}
            </span>
            <span data-test='transaction-details-PDF-amount-value' className='value'>
              <span data-test='amount'>
                {formatFloat(
                  transaction.amount * (transaction?.direction === TransactionDirections.WITHDRAW ? -1 : 1),
                  getCurrencyPrecision(transaction?.currency)
                )}{' '}
                <span className='transaction-currency'>{formatCurrencyForDisplay(transaction?.currency)}</span>
              </span>
              <div className='status'>
                <CoreStatusView
                  fade={true}
                  data-test='transaction-details-status'
                  status={transaction?.status as TransactionStatus}
                  label={
                    transaction?.documentRequest
                      ? tr('FRONTEND.TRANSACTIONS.DOCUMENT_REQUEST.STATUS_LABEL', 'Doc Required')
                      : undefined
                  }
                />
              </div>
            </span>
          </div>
        )}

        {Boolean(transaction?.documentRequest) && (
          <div className='document-request-banner' data-test='document-request-banner'>
            <div className='document-request-banner-left-side'>
              <div className='document-request-banner-icon'>
                <AnnounceIcon />
              </div>
              <p>
                {transaction.documentRequest.comment ||
                  tr('FRONTEND.TRANSACTIONS.DOCUMENT_REQUEST.DEFAULT_COMMENT', 'Please upload the requested document.')}
              </p>
            </div>
            <MMButton
              type='button'
              variant='secondary'
              caption={tr('FRONTEND.TRANSACTIONS.DOCUMENT_REQUEST.UPLOAD.TITLE', 'Upload Document')}
              onClick={() =>
                modalShow({
                  header: tr('FRONTEND.TRANSACTIONS.DOCUMENT_REQUEST.UPLOAD.TITLE', 'Upload Document'),
                  content: <DocumentRequestUploadModal docRequestId={transaction.documentRequest.id} />
                })
              }
              data-test='button-upload-document'
            />
          </div>
        )}

        {!transactionFetching && (
          <>
            <div className='transaction-details-content-wrapper'>
              <div className='transaction-details-inner'>
                <CorePreviewLine
                  className='transaction-details-single-content'
                  iconType='category-transaction'
                  title={tr('FRONTEND.TRANSACTIONS.DETAILS.TYPE', 'Type')}
                  text={tr(
                    transaction?.trnTypeLocalizationKey,
                    cleanupUnderscores(transaction?.transactionCategory).toLocaleLowerCase()
                  )}
                />
                {accountingCategoryFF.isEnabled && transaction?.accountingCategory && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='category-transaction'
                    title={tr('FRONTEND.WITHDRAW.CRYPTO.PREVIEW.CATEGORY', 'Payment category')}
                    text={getAccountingCategoryLabel(transaction.accountingCategory)}
                  />
                )}
                <CorePreviewLine
                  className='transaction-details-single-content'
                  iconType='date'
                  title={tr('FRONTEND.TRANSACTIONS.DETAILS.DATE', 'Date')}
                  text={formatDate(transaction?.reqTime)}
                />
                {Boolean(authorizationDate) && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='date'
                    title={tr('FRONTEND.TRANSACTIONS.DETAILS.AUTHORIZATION_DATE', 'Authorization Date')}
                    text={formatDateTime(authorizationDate)}
                  />
                )}
                {Boolean(transaction?.feeDetails?.amount) && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='percentage'
                    title={tr('FRONTEND.TRANSACTIONS.DETAILS.FEE', 'Fee')}
                    text={
                      <p>
                        {formatFloat(
                          transaction.feeDetails.amount,
                          getCurrencyPrecision(transaction.feeDetails?.currency)
                        )}{' '}
                        <span className='amount-currency'>
                          {formatCurrencyForDisplay(transaction.feeDetails?.currency)}
                        </span>
                      </p>
                    }
                  />
                )}
                {transaction?.promoCode && Number(transaction?.amountSaved) > 0 && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='percentage'
                    title={tr('FRONTEND.TRANSACTIONS.DETAILS.DISCOUNT', 'Discount')}
                    text={
                      <p className='discount-value'>
                        {formatFloat(
                          transaction?.amountSaved,
                          getCurrencyPrecision(transaction?.feeDetails?.currency ?? transaction?.currency)
                        )}{' '}
                        <span className='amount-currency'>
                          {formatCurrencyForDisplay(transaction?.feeDetails?.currency ?? transaction?.currency)}
                        </span>
                        {` ${tr('FRONTEND.TRANSACTIONS.DETAILS.SAVED_WITH_CODE', 'saved with code')} ${transaction?.promoCode}`}
                      </p>
                    }
                  />
                )}
                {transaction?.bankwireDetails?.counterpartyName && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='recipient'
                    title={
                      transaction?.direction === TransactionDirections.WITHDRAW
                        ? tr('FRONTEND.EXCHANGE.TO', 'To')
                        : tr('FRONTEND.EXCHANGE.FROM', 'From')
                    }
                    text={transaction.bankwireDetails.counterpartyName}
                  />
                )}
                {transaction?.accountName && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='account'
                    title={tr('FRONTEND.TRANSFER.PREVIEW.ACCOUNT', 'Account')}
                    text={transaction.accountName}
                  />
                )}
                {transaction?.checkoutDetails?.customerName && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='account'
                    title={tr('FRONTEND.TRANSACTIONS.DETAILS.CUSTOMER_NAME', 'Customer Name')}
                    text={transaction.checkoutDetails.customerName}
                  />
                )}
                {Boolean(transaction?.id) && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='transaction-id'
                    title={tr('FRONTEND.PENDING_APPROVALS.MODAL.TRANSACTION_ID', 'Transaction ID')}
                    text={transaction.id.toString()}
                  />
                )}

                {showProcessingAmount && (
                  <>
                    <CorePreviewLine
                      className='transaction-details-single-content'
                      iconType='transaction-id'
                      title={tr('FRONTEND.TRANSACTIONS.DETAILS.PAID_AMOUNT', 'Paid Amount')}
                      text={
                        <p>
                          {formatFloat(
                            transaction?.cardPaymentDetails?.transactionAmount,
                            getCurrencyPrecision(transaction?.cardPaymentDetails?.transactionCurrency)
                          )}{' '}
                          <span className='amount-currency'>
                            {formatCurrencyForDisplay(transaction?.cardPaymentDetails?.transactionCurrency)}
                          </span>
                        </p>
                      }
                    />
                    <CorePreviewLine
                      className='transaction-details-single-content'
                      iconType='transaction-id'
                      title={tr('FRONTEND.TRANSACTIONS.DETAILS.EXCHANGE_RATE', 'Exchange Rate')}
                      text={transaction?.cardPaymentDetails?.displayFxRate}
                    />
                  </>
                )}

                {transaction?.paymentMethod && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='method'
                    title={tr('FRONTEND.PENDING_APPROVALS.MODAL.METHOD', 'Method')}
                    text={tr(
                      `METADATA.PAYMENT_OPTIONS.${transaction.paymentMethod}`,
                      cleanupUnderscores(transaction.paymentMethod).toLocaleLowerCase()
                    )}
                  />
                )}
                {transaction?.reference && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='reference'
                    title={tr('FRONTEND.TRANSACTIONS.DETAILS.REFERENCE', 'Reference')}
                    text={transaction.reference}
                  />
                )}
                {transaction?.bankwireDetails?.counterpartyIban && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='reference'
                    title={tr('FRONTEND.DEPOSIT.PREVIEW.IBAN', 'IBAN')}
                    text={transaction.bankwireDetails.counterpartyIban}
                  />
                )}
                {transaction?.bankwireDetails?.counterpartyAccountNumber && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='reference'
                    title={tr('FRONTEND.TRANSACTIONS.DETAILS.ACCOUNT_NUMBER', 'Account Number')}
                    text={transaction.bankwireDetails.counterpartyAccountNumber}
                  />
                )}
                {transaction?.bankwireDetails?.counterpartyBankCode && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='reference'
                    title={tr('FRONTEND.TRANSACTIONS.DETAILS.BANK_CODE', 'Bank code')}
                    text={transaction.bankwireDetails.counterpartyBankCode}
                  />
                )}
                {transaction?.bankwireDetails?.counterpartyBranchCode && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='reference'
                    title={tr('FRONTEND.TRANSACTIONS.DETAILS.BRANCH_CODE', 'Branch code')}
                    text={transaction.bankwireDetails.counterpartyBranchCode}
                  />
                )}
                {transactionType === 'INTRA_ACCOUNT_TRANSFER' &&
                  Boolean(transaction?.emoneyInternalTransferDetails) && (
                    <CorePreviewLine
                      className='transaction-details-single-content'
                      iconType='reference'
                      title={
                        transaction?.direction === TransactionDirections.WITHDRAW
                          ? tr('FRONTEND.EXCHANGE.TO', 'To')
                          : tr('FRONTEND.EXCHANGE.FROM', 'From')
                      }
                      text={tr('FRONTEND.TRANSACTIONS.ACCOUNT_ID', '{{account}}ID: {{id}}', {
                        account: transaction?.emoneyInternalTransferDetails?.accountName
                          ? `${transaction.emoneyInternalTransferDetails.accountName}  - `
                          : '',
                        id: transaction?.emoneyInternalTransferDetails?.stAccountId
                      })}
                    />
                  )}

                {transaction?.externalWalletDetails?.name && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='reference'
                    title={tr('FRONTEND.TRANSACTIONS.DETAILS.WALLET_NAME', 'External wallet name')}
                    text={transaction.externalWalletDetails.name}
                  />
                )}

                {transaction?.externalWalletDetails?.identifier && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='reference'
                    title={tr('FRONTEND.TRANSACTIONS.DETAILS.WALLET_ID', 'External wallet ID')}
                    text={transaction.externalWalletDetails.identifier}
                  />
                )}

                {transaction?.cryptoDetails?.txHash && (
                  <CorePreviewLine
                    className='transaction-details-single-content hash'
                    iconType='reference'
                    title={tr('FRONTEND.TRANSACTIONS.DETAILS.TRANSACTION_HASH', 'Transaction hash')}
                    text={
                      <HashLink
                        hash={cutCryptoAddress(transaction.cryptoDetails.txHash)}
                        link={transaction.cryptoDetails.blockchainExplorerUrl}
                      />
                    }
                    value={transaction.cryptoDetails.txHash}
                    isCopyVisible={true}
                  />
                )}

                {transaction?.cryptoDetails?.counterpartyAddress && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='reference'
                    title={tr('FRONTEND.TRANSACTIONS.DETAILS.CRYPTO_ADDRESS', 'Address')}
                    text={transaction.cryptoDetails.counterpartyAddress}
                  />
                )}

                {transaction?.paymentFlow !== PaymentFlows.TRANSFER && Boolean(transaction?.parentTransactionId) && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='reference'
                    title={tr('FRONTEND.TRANSACTIONS.DETAILS.RELATED_TRANSACTION', 'Related Transaction')}
                    text={
                      <p
                        style={{
                          textDecoration: 'none',
                          color: 'blue',
                          cursor: 'pointer'
                        }}
                        onClick={() => setCurrentTransactionId(transaction?.parentTransactionId)}
                      >
                        {transaction?.parentTransactionId}
                      </p>
                    }
                  />
                )}
                {isValidArray(transaction?.documents) && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    title={tr('FRONTEND.TRANSACTIONS.DETAILS.DOCUMENTS', 'Documents')}
                    text={transaction?.documents.map(el => (
                      <p
                        key={el?.id}
                        style={{
                          textDecoration: 'none',
                          color: 'blue',
                          cursor: 'pointer'
                        }}
                        onClick={() => downloadAttachment(el?.id, el?.fileName)}
                      >
                        {el?.fileName}
                      </p>
                    ))}
                  />
                )}
                {['EMONEY_TRANSFER_INCOMING', 'EMONEY_TRANSFER_OUTGOING'].includes(transactionType) && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='reference'
                    title={tr('FRONTEND.TRANSACTIONS.DETAILS.EMAIL', 'Email')}
                    text={transaction?.instrumentDetails}
                  />
                )}

                {transaction?.foreignTransactionId && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='reference'
                    title={tr('FRONTEND.TRANSACTIONS.DETAILS.FOREIGN_ID', 'Foreign ID')}
                    text={transaction?.foreignTransactionId}
                  />
                )}
                {Boolean(transaction?.processingAmount) && !showProcessingAmount && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='reference'
                    title={tr('FRONTEND.TRANSACTIONS.DETAILS.PROCESSING_AMOUNT', 'Processing amount')}
                    text={
                      <p>
                        {formatFloat(
                          transaction.processingAmount,
                          getCurrencyPrecision(transaction?.processingCurrency)
                        )}{' '}
                        <span className='amount-currency'>
                          {formatCurrencyForDisplay(transaction?.processingCurrency)}
                        </span>
                      </p>
                    }
                  />
                )}

                {transaction?.statusCodeDefaultDescription && (
                  <CorePreviewLine
                    className='transaction-details-single-content'
                    iconType='reference'
                    title={tr('FRONTEND.TRANSACTIONS.COLUMNS.STATUS_DESCRIPTION', 'Fail reason')}
                    text={
                      isValidString(transaction?.statusCodeLocalizationKey)
                        ? tr(transaction?.statusCodeLocalizationKey, transaction?.statusCodeDefaultDescription)
                        : transaction?.statusCodeDefaultDescription
                    }
                  />
                )}
              </div>
              <CorePreviewLine
                className='transaction-details-description-content'
                iconType='description'
                title={tr('FRONTEND.TRANSACTIONS.DETAILS.DESCRIPTION', 'Description')}
                text={formatTransactionDescriptionForDisplay(transaction)}
              />
            </div>

            <div className='transaction-details-actions-wrapper'>
              {isModal && (
                <div className='transaction-details-footer-PDF-button'>
                  {RECEIPT_METHODS.includes(transaction?.paymentMethod ?? '') && (
                    <CoreButton
                      data-test='transaction-details-PDF-download-button'
                      text={tr('FRONTEND.TRANSACTIONS.DETAILS.BUTTON.DOWNLOAD', 'Download as PDF')}
                      LeftIcon={<TransactionsExportDocumentIcon />}
                      onClick={() => downloadReceipt(transaction.id)}
                      isLoading={downloadReceiptFetching}
                      variation='tertiary'
                      size='large'
                      className='transaction-details-header-PDF-button'
                    />
                  )}
                  {showRefund && (
                    <CoreButton
                      data-test='transaction-refund-button'
                      text={tr('FRONTEND.TRANSACTIONS.REFUND_BUTTON', 'Refund')}
                      variation='primary'
                      LeftIcon={<RefundIcon />}
                      size='large'
                      onClick={handleRefund}
                    />
                  )}
                  {showPayout && (
                    <CoreButton
                      className='payout-button'
                      data-test='transaction-payout-button'
                      text={tr('FRONTEND.TRANSACTIONS.PAYOUT_BUTTON', 'Payout')}
                      variation='primary'
                      LeftIcon={<PayoutIcon />}
                      size='large'
                      onClick={handlePayout}
                    />
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default TransactionDetails

const ADDITIONAL_ACTION_FLOWS: string[] = [PaymentFlows.CHECKOUT, PaymentFlows.DEPOSIT]

const ADDITIONAL_ACTION_METHODS: string[] = [
  PaymentMethods.BANKWIRE,
  PaymentMethods.INSTANT_BANK_TRANSFER,
  PaymentMethods.INTERAC
]

const RECEIPT_METHODS: string[] = [
  PaymentMethods.BANKWIRE,
  PaymentMethods.SWIFT_BANKWIRE,
  PaymentMethods.LOCAL_BANKWIRE,
  PaymentMethods.SEPA_BANKWIRE
]
